import { Box, Stack, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardBehaviorChartLine,
  IDashboardBehaviorSeriesPoint,
} from "ts/interfaces/AdminDashboard"

import {
  formatBucket,
  formatBucketLong,
  formatCompactCount,
} from "./behaviorFormat"

interface IChartRow {
  label: string
  longLabel: string
  isPartial: boolean
  bonus: number
  fine: number
  bonusSolid: number | null
  fineSolid: number | null
  bonusPartial: number | null
  finePartial: number | null
}

// Each line is drawn twice: solid through the comparable buckets, dashed
// through a partial bucket and its neighbour, so the dashed segment connects.
const toChartRows = (points: IDashboardBehaviorSeriesPoint[]): IChartRow[] =>
  points.map((point, index) => {
    const touchesPartial =
      !point.is_comparable ||
      points[index - 1]?.is_comparable === false ||
      points[index + 1]?.is_comparable === false
    return {
      label: formatBucket(point.period_from, point.period_to),
      longLabel: formatBucketLong(point.period_from, point.period_to),
      isPartial: !point.is_comparable,
      bonus: point.bonus_count,
      fine: point.fine_count,
      bonusSolid: point.is_comparable ? point.bonus_count : null,
      fineSolid: point.is_comparable ? point.fine_count : null,
      bonusPartial: touchesPartial ? point.bonus_count : null,
      finePartial: touchesPartial ? point.fine_count : null,
    }
  })

// Draws the given lines over the series' bonus/fine counts; a single line
// (one behaviour) is named by the card above, so it gets no legend.
const BehaviorChart = ({
  points,
  lines,
  tableLabel,
}: {
  points: IDashboardBehaviorSeriesPoint[]
  lines: IDashboardBehaviorChartLine[]
  tableLabel: string
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const rows = toChartRows(points)
  const periodLabel = (row: IChartRow) =>
    row.isPartial
      ? t("adminDashboard.partialPeriod", { period: row.label })
      : row.label

  // A partial bucket gets a hollow dot; the null slots of the solid/dashed
  // split come through without coordinates and draw nothing.
  const renderDot = (color: string) =>
    function Dot({
      cx,
      cy,
      index,
      payload,
    }: {
      cx?: number | null
      cy?: number | null
      index?: number
      payload?: IChartRow
    }) {
      return cx == null || cy == null ? (
        <g key={index} />
      ) : (
        <circle
          key={index}
          cx={cx}
          cy={cy}
          r={3.5}
          stroke={color}
          strokeWidth={2}
          fill={payload?.isPartial ? theme.palette.mockup.neutral100 : color}
        />
      )
    }

  return (
    <Box>
      {lines.length > 1 && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={2}
          mb={1}
          aria-hidden="true"
        >
          {lines.map(({ key, color, name }) => (
            <Stack key={key} direction="row" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: color,
                }}
              />
              <Typography variant="body3" color="mockup.neutral30">
                {name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      <Box aria-hidden="true">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={rows}
            margin={{ top: 20, right: 16, left: 16, bottom: 0 }}
          >
            <CartesianGrid
              vertical
              horizontal={false}
              stroke={theme.palette.mockup.neutral90}
            />
            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              angle={-90}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 11, fill: theme.palette.mockup.neutral30 }}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ stroke: theme.palette.mockup.neutral90 }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as IChartRow | undefined
                if (!active || !row) return null
                return (
                  <Box
                    sx={{
                      minWidth: 220,
                      maxWidth: 320,
                      bgcolor: "mockup.neutral100",
                      border: "1px solid",
                      borderColor: "mockup.neutral90",
                      borderRadius: "12px",
                      boxShadow: 3,
                      p: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="mockup.neutral30"
                      fontWeight={500}
                    >
                      {row.longLabel}
                    </Typography>
                    {row.isPartial && (
                      <Typography
                        variant="body3"
                        display="block"
                        color="mockup.neutral30"
                        fontWeight={500}
                      >
                        {t("adminDashboard.partialPeriodNote")}
                      </Typography>
                    )}
                    <Stack
                      rowGap={0.75}
                      mt={1}
                      pt={1}
                      sx={{
                        borderTop: "1px solid",
                        borderColor: "mockup.neutral90",
                      }}
                    >
                      {lines.map(({ key, color, name }) => (
                        <Stack
                          key={key}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            minWidth={0}
                          >
                            <Box
                              sx={{
                                flexShrink: 0,
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: color,
                              }}
                            />
                            <Typography
                              variant="body3"
                              noWrap
                              color="mockup.neutral30"
                              fontWeight={500}
                            >
                              {name}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            color="mockup.neutral0"
                            fontWeight={700}
                          >
                            {row[key].toLocaleString("en-US")}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )
              }}
            />
            {lines.flatMap(({ key, color }) => [
              <Line
                key={`${key}Solid`}
                dataKey={`${key}Solid`}
                stroke={color}
                strokeWidth={2}
                isAnimationActive={false}
                connectNulls={false}
                dot={renderDot(color)}
              />,
              <Line
                key={`${key}Partial`}
                dataKey={`${key}Partial`}
                stroke={color}
                strokeWidth={2}
                strokeDasharray="4 4"
                isAnimationActive={false}
                connectNulls={false}
                dot={renderDot(color)}
              />,
              // An invisible full line carries the value labels, so each
              // point is labelled once whatever the solid/dashed split.
              <Line
                key={`${key}Labels`}
                dataKey={key}
                stroke="none"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={key}
                  position="top"
                  formatter={formatCompactCount}
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    fill: theme.palette.mockup.neutral30,
                  }}
                />
              </Line>,
            ])}
          </LineChart>
        </ResponsiveContainer>
      </Box>
      {/* The chart is a picture; screen readers get the same numbers here */}
      <Box component="table" aria-label={tableLabel} sx={visuallyHiddenSx}>
        <thead>
          <tr>
            <th scope="col">{t("adminDashboard.period")}</th>
            {lines.map(({ key, name }) => (
              <th key={key} scope="col">
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{periodLabel(row)}</th>
              {lines.map(({ key }) => (
                <td key={key}>{row[key].toLocaleString("en-US")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

export default BehaviorChart
